import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, List, Modal, Space, Tag, Typography, message } from 'antd';
import { DeleteOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FindingsStrip from './FindingsStrip';
import { calculateFindings } from './documentFindings';
import { parseCustomFieldValueForForm } from './customFieldTypes';
import {
  SYNC_FAILED,
  SYNC_LOCAL,
  SYNC_SYNCING,
  deleteDraft,
  getDrafts,
} from '../../utils/documentDraftStore';
import { enqueueDraft, flushOutbox, subscribeToOutbox } from '../../utils/documentOutbox';

const { Text } = Typography;

const STATE = {
  [SYNC_LOCAL]: { color: 'default', label: 'Draft' },
  [SYNC_SYNCING]: { color: 'processing', label: 'Uploading' },
  [SYNC_FAILED]: { color: 'error', label: 'Failed' },
};

const stateOf = (draft) => STATE[draft.syncState] ?? { color: 'blue', label: 'Queued' };

// The draft holds its answers in the server's shape, so the same helpers the
// library uses can score it without it ever having been uploaded.
const findingsFor = (draft) => {
  const fields = draft.form?.fields ?? [];
  if (!draft.form?.hasRating || fields.length === 0) return null;

  // Table rows repeat a field id, so only the plain answers are mapped here.
  const byId = new Map(
    (draft.fieldValues ?? [])
      .filter((entry) => entry.rowIndex == null)
      .map((entry) => [entry.documentTemplateCustomFieldId, entry.fieldValues])
  );

  return calculateFindings(fields, (field) =>
    parseCustomFieldValueForForm(field, byId.get(field.id))
  );
};

const PendingDocuments = ({ typeName = null }) => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);

  const reload = useCallback(() => {
    getDrafts()
      .then((held) =>
        setDrafts(
          typeName
            ? held.filter(
                (draft) =>
                  String(draft.form?.typeName ?? '').trim().toLowerCase() ===
                  typeName.trim().toLowerCase()
              )
            : held
        )
      )
      .catch((error) => console.error('Failed to read documents held on this device', error));
  }, [typeName]);

  useEffect(() => {
    reload();

    // Covers the case the app-start flush cannot: signing in with work already
    // queued from a previous session.
    flushOutbox();

    return subscribeToOutbox(reload);
  }, [reload]);

  if (drafts.length === 0) return null;

  const handleOpen = (draft) => {
    navigate(
      draft.documentId ? `/documents/${draft.documentId}` : `/documents/fill/${draft.templateId}`,
      { state: { ...(draft.router ?? {}), draftId: draft.draftId } }
    );
  };

  const handleRetry = async (draft) => {
    try {
      await enqueueDraft(draft.draftId);
    } catch (error) {
      console.error('Failed to queue document', error);
      message.error('Could not queue this document');
    }
  };

  const handleDiscard = (draft) => {
    Modal.confirm({
      title: 'Discard this document?',
      content: 'It has not been uploaded, so everything in it is lost.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteDraft(draft.draftId);
        reload();
      },
    });
  };

  return (
    <Card
      className="document-library__pending"
      title="On this device"
      extra={
        <Text type="secondary" className="document-library__pending-count">
          {drafts.length}
        </Text>
      }
    >
      <List
        className="document-list"
        dataSource={drafts}
        renderItem={(draft) => {
          const state = stateOf(draft);
          const findings = findingsFor(draft);
          const meta = [draft.router?.templateName, draft.form?.companies?.find(
            (company) => company.id === draft.companyId
          )?.name].filter(Boolean).join(' · ');

          return (
            <List.Item
              className="document-list__item"
              onClick={() => handleOpen(draft)}
              role="button"
            >
              <List.Item.Meta
                avatar={
                  <FileTextOutlined
                    className={`document-list__icon document-list__icon--${draft.syncState}`}
                  />
                }
                title={
                  <span className="document-list__title">{draft.name || 'Untitled document'}</span>
                }
                description={
                  <Space direction="vertical" size={2} className="document-list__meta">
                    {meta && <Text type="secondary">{meta}</Text>}

                    <Space size={6} wrap>
                      <Tag color={state.color}>{state.label}</Tag>
                      {draft.updatedOn && (
                        <Text type="secondary">
                          {dayjs(draft.updatedOn).format('DD MMM YYYY')}
                        </Text>
                      )}
                    </Space>

                    {draft.lastError && (
                      <Text type="danger" className="document-list__error">
                        {draft.lastError}
                      </Text>
                    )}
                  </Space>
                }
              />

              <div className="document-list__actions">
                <Space size={4}>
                  {draft.syncState === SYNC_FAILED && (
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRetry(draft);
                      }}
                    >
                      Retry
                    </Button>
                  )}

                  <Button
                    size="small"
                    aria-label="Discard"
                    icon={<DeleteOutlined />}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDiscard(draft);
                    }}
                  />
                </Space>

                {findings && (
                  <div className="document-list__findings">
                    <FindingsStrip variant="circles" counts={findings} />
                  </div>
                )}
              </div>
            </List.Item>
          );
        }}
      />
    </Card>
  );
};

export default PendingDocuments;
